import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React from 'react';
import { useSelector } from 'react-redux';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type RTWFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: RTWFilterValues) => void;
};

export type RTWFilterValues = {
  companies?: Option<string>[];
  agencies?: Option<string>[];
  taxYear?: number;
};

const RTWFilterModal: React.FC<RTWFilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
}: RTWFilterModalProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();
  const { isAgency } = useSelector((state: AppState) => state.appStore);

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const taxYear = Form.useWatch('taxYear', form);
  const ableToClearFilter =
    watchValue &&
    ((watchValue.companies?.length ?? 0) > 0 ||
      (watchValue.agencies?.length ?? 0) > 0 ||
      !!watchValue.taxYear);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {isAgency && (
          <Form.Item label={messages['common.taxYear']} name="taxYear">
            <TaxYearSelect
              initValue={taxYear}
              noDefaultValue
              allowClear
              onFilter={(value) => {
                form.setFieldsValue({
                  taxYear: value.taxYear,
                });
              }}
            />
          </Form.Item>
        )}

        <Form.Item label={employerLabels.label} name="companies">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            mode="multiple"
            placeholder={messages['form.pleaseSelect']}
            onChangeValue={(value) => {
              form.setFieldsValue({
                companies: value,
              });
            }}
          />
        </Form.Item>

        {!isAgency && (
          <Form.Item label={messages['common.agency']} name="agencies">
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              mode="multiple"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  agencies: value,
                });
              }}
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default RTWFilterModal;
